// Icon picker — a trigger-anchored popover with a searchable, category-filterable
// grid of icons. The grid is fetched once from the server on first open (so the
// ~1770 glyphs never ship on page load), then filtered entirely client-side:
// search matches each cell's data-search (icon name + lucide tags), and the
// category chips match data-categories. Selecting a cell writes the canonical
// value to the hidden input the form submits and updates the trigger glyph.
//
// The popover is position: fixed and placed under the trigger (flipping above
// when there's no room), so it escapes the card's overflow without a modal.
//
// <div data-controller="icon-picker" data-icon-picker-src-value="/settings/icons">

import { Controller } from "@hotwired/stimulus"

// Grid HTML cache shared across every picker instance and Turbo navigation (the
// JS runtime survives Turbo visits), so the grid is fetched once per session
// rather than on every open. Only the version-stable no-custom grid is cached;
// the per-community `?custom=1` variant is always fetched fresh so newly added
// custom icons show up.
const GRID_CACHE = new Map()

export default class extends Controller {
  static targets = [
    "input", "trigger", "triggerGlyph", "triggerLabel",
    "popover", "search", "chip", "gridContainer", "grid", "cell", "loading", "empty",
  ]
  static values = {
    src: String,
    selected: { type: String, default: "" },
  }

  connect() {
    this.loaded = false
    this.activeCategory = ""
    this.highlightIndex = -1
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this.onReposition = this.onReposition.bind(this)
  }

  disconnect() {
    this.stopListening()
  }

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  isOpen() {
    return !this.popoverTarget.classList.contains("hidden")
  }

  open() {
    this.popoverTarget.classList.remove("hidden")
    this.searchTarget.value = ""
    this.activeCategory = ""
    this.syncChips()
    this.position()
    this.loadGrid()
    this.searchTarget.focus()
    document.addEventListener("click", this.onDocumentClick, true)
    document.addEventListener("keydown", this.onDocumentKeydown, true)
    window.addEventListener("scroll", this.onReposition, true)
    window.addEventListener("resize", this.onReposition)
  }

  close() {
    this.popoverTarget.classList.add("hidden")
    this.stopListening()
  }

  stopListening() {
    document.removeEventListener("click", this.onDocumentClick, true)
    document.removeEventListener("keydown", this.onDocumentKeydown, true)
    window.removeEventListener("scroll", this.onReposition, true)
    window.removeEventListener("resize", this.onReposition)
  }

  onDocumentClick(event) {
    if (!this.popoverTarget.contains(event.target) && !this.triggerTarget.contains(event.target)) {
      this.close()
    }
  }

  onDocumentKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    }
  }

  onReposition() {
    if (this.isOpen()) this.position()
  }

  // Place the popover under the trigger (flip above when it would overflow the
  // viewport), clamped horizontally. Fixed coords, so overflow-hidden ancestors
  // don't clip it.
  position() {
    const rect = this.triggerTarget.getBoundingClientRect()
    const pop = this.popoverTarget
    const gap = 4
    const width = pop.offsetWidth
    const height = pop.offsetHeight

    let top = rect.bottom + gap
    if (top + height > window.innerHeight - 8 && rect.top - gap - height > 8) {
      top = rect.top - gap - height
    }

    let left = rect.left
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8)
    }

    pop.style.top = `${Math.max(8, top)}px`
    pop.style.left = `${left}px`
  }

  async loadGrid() {
    if (this.loaded) {
      this.applyFilter()
      return
    }

    const cacheable = !this.srcValue.includes("custom=1")
    const cached = cacheable ? GRID_CACHE.get(this.srcValue) : null
    if (cached) {
      this.insertGrid(cached)
      return
    }

    try {
      const response = await fetch(this.srcValue, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(response.status)
      const html = await response.text()
      if (cacheable) GRID_CACHE.set(this.srcValue, html)
      this.insertGrid(html)
    } catch {
      this.loadingTarget.textContent = "Couldn't load icons. Please try again."
    }
  }

  insertGrid(html) {
    this.loadingTarget.insertAdjacentHTML("beforebegin", html)
    this.loaded = true
    this.loadingTarget.classList.add("hidden")
    this.markSelected()
    this.applyFilter()
    this.position()
  }

  filter() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this.applyFilter(), 120)
  }

  filterCategory(event) {
    this.activeCategory = event.currentTarget.dataset.category || ""
    this.syncChips()
    this.applyFilter()
  }

  applyFilter() {
    const terms = this.searchTarget.value.toLowerCase().trim().split(/\s+/).filter(Boolean)
    let visible = 0

    this.cellTargets.forEach((cell) => {
      const matchesQuery = terms.every((t) => cell.dataset.search.includes(t))
      const matchesCategory = this.matchesCategory(cell)
      const show = matchesQuery && matchesCategory
      cell.classList.toggle("hidden", !show)
      if (show) visible++
    })

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", visible > 0)
    this.clearHighlight()
  }

  matchesCategory(cell) {
    if (!this.activeCategory) return cell.dataset.categories !== "__none__" // "All" hides the None cell
    return cell.dataset.categories.split(" ").includes(this.activeCategory)
  }

  select(event) {
    this.choose(event.currentTarget)
  }

  choose(cell) {
    const value = cell.dataset.value || ""
    this.inputTarget.value = value
    this.triggerGlyphTarget.innerHTML = cell.innerHTML
    this.triggerLabelTarget.textContent = cell.getAttribute("title") || "None"
    this.markSelected()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
    this.triggerTarget.focus()
  }

  markSelected() {
    const current = this.inputTarget.value || ""
    this.cellTargets.forEach((cell) => {
      cell.toggleAttribute("data-selected", (cell.dataset.value || "") === current)
    })
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.chooseHighlighted()
      return
    }

    const columns = this.columnCount()
    const delta = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }[event.key]
    if (delta === undefined) return

    event.preventDefault()
    this.moveHighlight(delta)
  }

  moveHighlight(delta) {
    const visible = this.visibleCells()
    if (visible.length === 0) return

    const start = this.highlightIndex < 0 ? 0 : this.highlightIndex + delta
    this.highlightIndex = Math.max(0, Math.min(visible.length - 1, start))
    this.renderHighlight(visible)
  }

  chooseHighlighted() {
    const visible = this.visibleCells()
    if (this.highlightIndex >= 0 && visible[this.highlightIndex]) {
      this.choose(visible[this.highlightIndex])
    } else if (visible.length === 1) {
      this.choose(visible[0])
    }
  }

  renderHighlight(visible) {
    visible.forEach((cell, i) => {
      cell.classList.toggle("ring-2", i === this.highlightIndex)
      cell.classList.toggle("ring-blue-500", i === this.highlightIndex)
      if (i === this.highlightIndex) cell.scrollIntoView({ block: "nearest" })
    })
  }

  clearHighlight() {
    this.highlightIndex = -1
    this.cellTargets.forEach((cell) => cell.classList.remove("ring-2", "ring-blue-500"))
  }

  visibleCells() {
    return this.cellTargets.filter((cell) => !cell.classList.contains("hidden"))
  }

  columnCount() {
    if (!this.hasGridTarget) return 1
    const cols = getComputedStyle(this.gridTarget).gridTemplateColumns.split(" ").filter(Boolean).length
    return cols || 1
  }

  syncChips() {
    this.chipTargets.forEach((chip) => {
      chip.setAttribute("aria-pressed", String((chip.dataset.category || "") === this.activeCategory))
    })
  }
}
